import { Card, Grid, Icon, IconButton, InputLabel, Switch, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import MDInput from "components/MDInput";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDSnackbar from "components/MDSnackbar";
import { useMaterialUIController } from "context";
import DataTable from "examples/Tables/DataTable";
import DefaultCell from "layouts/dashboards/sales/components/DefaultCell";
import { useCallback, useEffect, useRef, useState } from "react";
import { verifyVariable } from "utils/Functions";
import { get, post } from "utils/WebService";
import MarcacaoEspecialCreateEdit from "./MarcacaoEspecialCreateEdit";
import LoteAssociacaoModal from "./LoteAssociacaoModal";
import MDAuditModal from "components/MDAuditModal";
import { ESCURO } from "assets/coresTema";

export default function MarcacaoEspecialConsult() {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;

    const [auditModalOpen, setAuditModalOpen] = useState(false);
    const [auditTitle, setAuditTitle] = useState("");
    const [auditLogs, setAuditLogs] = useState([]);
    const [loadingAudit, setLoadingAudit] = useState(false);

    const handleOpenAuditoriaRow = (row) => {
        const id = row.cvId;
        setAuditTitle(`Auditoria da Marcação: ${row.ccNome || ("ID " + id)}`);
        setAuditModalOpen(true);
        setLoadingAudit(true);
        get(`/marcacoes/${id}/historico`)
            .then(res => setAuditLogs(Array.isArray(res) ? res : []))
            .catch(err => {
                console.error("Erro ao carregar auditoria da marcação:", err);
                setAuditLogs([]);
            })
            .finally(() => setLoadingAudit(false));
    };

    const handleOpenAuditoriaGeral = () => {
        setAuditTitle("Histórico de Auditoria - Marcações Especiais");
        setAuditModalOpen(true);
        setLoadingAudit(true);
        get("/marcacoes/historico-geral")
            .then(res => setAuditLogs(Array.isArray(res) ? res : []))
            .catch(err => {
                console.error("Erro ao carregar auditoria geral de marcações:", err);
                setAuditLogs([]);
            })
            .finally(() => setLoadingAudit(false));
    };

    const tableState = useRef({
        pageSize: 10,
        pageIndex: 0,
        sorting: [],
        filtered: [],
    });

    const [data, setData] = useState([]);
    const [pages, setPages] = useState(0);
    const [loadingTable, setLoadingTable] = useState(true);
    const [isCreateEdit, setIsCreateEdit] = useState(false);
    const [isLoteModal, setIsLoteModal] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [recordsTotal, setRecordsTotal] = useState(0);

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);

    const [successSB, setSuccessSB] = useState(false);
    const [errorSB, setErrorSB] = useState(false);
    const [sbMessage, setSbMessage] = useState("");

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = useCallback(() => {
        setLoadingTable(true);
        get("/marcacoes").then((res) => {
            setData(res);
            setRecordsTotal(res.length);
            setPages(1);
            setLoadingTable(false);
        }).catch(() => {
            setLoadingTable(false);
        });
    }, []);

    const backFunction = () => {
        fetchData();
        setIsCreateEdit(false);
        setSelectedId(null);
    }

    const handleToggleAtivo = (row, newStatus) => {
        const rowId = row.cvId;
        const payload = {
            ccNome: row.ccNome,
            cvAlvo: row.cvAlvo,
            cvAtivo: newStatus
        };
        post(`/marcacoes/${rowId}`, payload)
            .then(() => {
                setSbMessage(`Marcação "${row.ccNome}" ${newStatus ? "ativada" : "inativada"} com sucesso!`);
                setSuccessSB(true);
                fetchData();
            })
            .catch((err) => {
                const errorMsg = err?.response?.data?.message || err?.data?.message || err?.message || "Erro ao alterar status da marcação.";
                setSbMessage(errorMsg);
                setErrorSB(true);
            });
    };

    const handleDeleteClick = (row) => {
        setItemToDelete(row);
        setDeleteDialogOpen(true);
    };

    const confirmDelete = () => {
        if (!itemToDelete) return;
        const rowId = itemToDelete.cvId;
        post(`/marcacoes/delete/${rowId}`, {})
            .then(() => {
                setSbMessage(`Marcação "${itemToDelete.ccNome}" excluída com sucesso!`);
                setSuccessSB(true);
                fetchData();
            })
            .catch((err) => {
                const errorMsg = err?.response?.data?.message || err?.data?.message || err?.message || "Erro ao excluir marcação especial.";
                setSbMessage(errorMsg);
                setErrorSB(true);
            })
            .finally(() => {
                setDeleteDialogOpen(false);
                setItemToDelete(null);
            });
    };

    const getAlvoName = (alvo) => {
        switch(alvo) {
            case 1: return "Ficha (Devedor)";
            case 2: return "Contrato";
            case 3: return "Ambos";
            default: return "Desconhecido";
        }
    }

    const columns = [
        {
            header: "nome",
            accessorKey: "ccNome",
            cell: info => <DefaultCell>{info.getValue() || ""}</DefaultCell>,
            isSorted: true
        },
        {
            header: "aplicação",
            accessorKey: "cvAlvo",
            cell: info => <DefaultCell>{getAlvoName(info.getValue())}</DefaultCell>,
            isSorted: true
        },
        {
            header: "carteiras",
            accessorKey: "carteiras",
            cell: info => {
                const row = info.row.original;
                return <DefaultCell>{row.universal || !(row.carteiras || []).length ? "Todas (universal)" : row.carteiras.join(", ")}</DefaultCell>;
            },
            isSorted: false
        },
        {
            header: "ativo",
            accessorKey: "cvAtivo",
            cell: info => {
                const row = info.row.original;
                const isChecked = !!info.getValue();
                return (
                    <MDBox display="flex" alignItems="center">
                        <Switch
                            checked={isChecked}
                            onChange={(e) => handleToggleAtivo(row, e.target.checked)}
                            color="info"
                        />
                    </MDBox>
                );
            },
            isSorted: false
        },
        {
            header: "ações",
            isSorted: false,
            cell: info => {
                const row = info.row.original;
                return (
                    <MDBox display="flex" alignItems="center">
                        <Tooltip title="Histórico de Auditoria">
                            <IconButton onClick={() => handleOpenAuditoriaRow(row)}>
                                <MDTypography variant="h4">
                                    <Icon color="info">history</Icon>
                                </MDTypography>
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Editar">
                            <IconButton
                                onClick={() => {
                                    const rowId = row.cvId;
                                    setSelectedId(rowId);
                                    setIsCreateEdit(true);
                                }}>
                                <MDTypography variant="h4">
                                    <Icon color="inherit" sx={{ marginRight: 1 }}>edit</Icon>
                                </MDTypography>
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir">
                            <IconButton
                                onClick={() => handleDeleteClick(row)}>
                                <MDTypography variant="h4">
                                    <Icon color="error">delete</Icon>
                                </MDTypography>
                            </IconButton>
                        </Tooltip>
                    </MDBox>
                );
            },
            width: "25%"
        },
    ];

    return (
        <Grid container display="flex" justifyContent="center">
            {isCreateEdit &&
                <Grid item xs={12} md={12}>
                    <MarcacaoEspecialCreateEdit id={selectedId} closeCreateEdit={backFunction} dataOriginal={data.find(d => d.cvId === selectedId)} />
                </Grid>
            }
            {isLoteModal &&
                <LoteAssociacaoModal open={isLoteModal} handleClose={() => setIsLoteModal(false)} marcacaoId={selectedId} marcacaoObj={data.find(d => d.cvId === selectedId)} />
            }
            <Grid item xs={12} md={12} sx={{ display: isCreateEdit ? "none" : "" }}>
                <Card variant="outlined" sx={{ px: 2, py: 1.5, mb: 2, background: darkMode ? ESCURO.cartao : "#fafafa", borderColor: darkMode ? ESCURO.divisoria : "#ddd" }}>
                    <Grid container spacing={1}>
                        <Grid item xs={12} display="flex" alignItems="center" justifyContent="space-between" pt={1}>
                            <MDBox display="flex" gap={1}>
                                <MDButton 
                                    size="medium" 
                                    variant="contained" 
                                    color="info" 
                                    onClick={() => { setSelectedId(null); setIsCreateEdit(true); }}
                                    sx={{
                                        backgroundColor: darkMode ? "#007bff !important" : undefined,
                                        color: "#ffffff !important"
                                    }}
                                >
                                    Adicionar
                                </MDButton>
                                <MDButton 
                                    size="medium" 
                                    variant="outlined" 
                                    color="info" 
                                    onClick={handleOpenAuditoriaGeral}
                                >
                                    <Icon sx={{ mr: 0.5 }}>history</Icon> Auditoria Geral
                                </MDButton>
                                <MDButton 
                                    size="medium" 
                                    variant="contained" 
                                    color="warning" 
                                    onClick={() => { setSelectedId(null); setIsLoteModal(true); }}
                                    sx={{
                                        backgroundColor: "#f57c00 !important",
                                        color: "#ffffff !important",
                                        fontWeight: "bold"
                                    }}
                                >
                                    <Icon sx={{ mr: 0.5 }}>bookmarks</Icon>
                                    Marcação em Bloco
                                </MDButton>
                            </MDBox>
                        </Grid>
                    </Grid>
                </Card>
                <DataTable loading={loadingTable} columnData={columns} data={data} fetchData={fetchData} totalPages={pages} totalRecords={recordsTotal} changeFilter={false} tableState={tableState} />
            </Grid>

            {/* Modal de Confirmação de Exclusão */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle sx={{ backgroundColor: darkMode ? ESCURO.painel : "#fff", color: darkMode ? "#fff" : "inherit" }}>
                    Confirmar Exclusão
                </DialogTitle>
                <DialogContent sx={{ backgroundColor: darkMode ? ESCURO.painelEscuro : "#fff", color: darkMode ? "#fff" : "inherit" }}>
                    Tem certeza que deseja excluir a Marcação Especial <strong>{itemToDelete?.ccNome}</strong>?
                </DialogContent>
                <DialogActions sx={{ backgroundColor: darkMode ? ESCURO.painel : "#fff" }}>
                    <MDButton onClick={() => setDeleteDialogOpen(false)} color="secondary" variant="outlined" sx={{ color: darkMode ? "#fff !important" : undefined, borderColor: darkMode ? "#fff !important" : undefined }}>
                        Cancelar
                    </MDButton>
                    <MDButton onClick={confirmDelete} color="error" variant="contained" sx={{ fontWeight: "bold" }}>
                        Excluir
                    </MDButton>
                </DialogActions>
            </Dialog>

            {/* Modal de Auditoria */}
            <MDAuditModal
                open={auditModalOpen}
                onClose={() => setAuditModalOpen(false)}
                title={auditTitle}
                historicos={auditLogs}
                loading={loadingAudit}
                entityName="Marcação Especial"
            />

            {/* Feedback / Notificações */}
            <MDSnackbar
                color={successSB ? "success" : "error"}
                icon={successSB ? "check" : "warning"}
                title={successSB ? "Sucesso" : "Atenção"}
                content={sbMessage}
                dateTime=""
                open={successSB || errorSB}
                onClose={() => { setSuccessSB(false); setErrorSB(false); }}
                close={() => { setSuccessSB(false); setErrorSB(false); }}
                bgWhite
            />
        </Grid>
    );
}
